import type { Notification } from '@lombok/shared';
import { Badge } from '@lombok/ui';
import type { BadgeTone } from '@lombok/ui';
import { notificationTone, notificationTypeLabel, relativeTimeEs } from './labels';

interface NotificationItemProps {
  notification: Notification;
  /** Called when the user taps to mark this one read (only shown when unread). */
  onMarkRead?: (id: string) => void;
  /** Compact variant for the bell dropdown (tighter padding). */
  compact?: boolean;
}

/**
 * A single notification row used by both the bell dropdown and the full page.
 * Unread items get an accent dot + a subtle background and a "Marcar leída"
 * affordance; the type drives a small Spanish category chip + tone. The
 * notification's own `title`/`body` (already Spanish from the backend) lead;
 * the type label is the fallback when no title is set.
 */
export function NotificationItem({ notification: n, onMarkRead, compact }: NotificationItemProps) {
  const unread = !n.readAt;
  const title = n.title?.trim() || notificationTypeLabel(n.type);

  return (
    <div
      className={`flex gap-3 ${compact ? 'px-3 py-2.5' : 'px-4 py-3.5'} ${
        unread ? 'bg-accent-soft/40' : ''
      }`}
    >
      {/* Unread dot (keeps a fixed gutter so read/unread align). */}
      <span className="mt-1.5 flex h-2 w-2 shrink-0 items-center justify-center">
        {unread ? <span className="h-2 w-2 rounded-full bg-brand-primary" aria-hidden="true" /> : null}
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-start justify-between gap-2">
          <p className={`min-w-0 text-sm ${unread ? 'font-semibold' : 'font-medium'} text-text-primary`}>
            {title}
          </p>
          <span className="shrink-0 whitespace-nowrap text-xs text-text-muted">
            {relativeTimeEs(n.createdAt)}
          </span>
        </div>

        {n.body ? (
          <p className={`text-sm text-text-muted ${compact ? 'line-clamp-2' : ''}`}>{n.body}</p>
        ) : null}

        <div className="mt-0.5 flex items-center gap-3">
          <Badge tone={toneToBadge(notificationTone(n.type))}>{notificationTypeLabel(n.type)}</Badge>
          {unread && onMarkRead ? (
            <button
              type="button"
              onClick={() => onMarkRead(n.notificationId)}
              className="text-xs font-medium text-brand-primary hover:underline"
            >
              Marcar leída
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** Map a notification tone to a Badge tone (1:1 today; kept explicit for safety). */
function toneToBadge(tone: ReturnType<typeof notificationTone>): BadgeTone {
  switch (tone) {
    case 'success':
      return 'success';
    case 'warning':
      return 'warning';
    case 'danger':
      return 'danger';
    case 'info':
      return 'info';
    default:
      return 'neutral';
  }
}
